import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

interface PaisDestino {
  id: string;
  nombre: string;
  coordenadas: {
    x: number;
    y: number;
  };
  color: string;
}

const paisesDestino: PaisDestino[] = [
  { 
    id: 'FR', 
    nombre: 'Francia', 
    coordenadas: { x: 520, y: 280 },
    color: '#FF6B6B'
  },
  { 
    id: 'DE', 
    nombre: 'Alemania', 
    coordenadas: { x: 580, y: 250 },
    color: '#4ECDC4'
  },
  { 
    id: 'IT', 
    nombre: 'Italia', 
    coordenadas: { x: 550, y: 320 },
    color: '#45B7D1'
  },
  { 
    id: 'PT', 
    nombre: 'Portugal', 
    coordenadas: { x: 420, y: 350 },
    color: '#96CEB4'
  },
  { 
    id: 'GB', 
    nombre: 'Reino Unido', 
    coordenadas: { x: 450, y: 220 },
    color: '#FFEEAD'
  },
  { 
    id: 'NL', 
    nombre: 'Países Bajos', 
    coordenadas: { x: 520, y: 230 },
    color: '#D4A5A5'
  },
  { 
    id: 'BE', 
    nombre: 'Bélgica', 
    coordenadas: { x: 500, y: 240 },
    color: '#9B59B6'
  },
  { 
    id: 'CH', 
    nombre: 'Suiza', 
    coordenadas: { x: 530, y: 290 },
    color: '#3498DB'
  }
];

const EnviosPagos = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const linesRef = useRef<SVGGElement>(null);
  const espanaCoords = { x: 470, y: 330 };

  useEffect(() => {
    if (!svgRef.current || !linesRef.current) return;

    // Crear flechas animadas para cada país destino
    paisesDestino.forEach((pais) => {
      // Crear el grupo para la flecha
      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      group.style.opacity = '0';
      linesRef.current?.appendChild(group);

      // Calcular el ángulo de la flecha
      const dx = pais.coordenadas.x - espanaCoords.x;
      const dy = pais.coordenadas.y - espanaCoords.y;
      const angle = Math.atan2(dy, dx) * 180 / Math.PI;

      // Crear la línea de la flecha
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', espanaCoords.x.toString());
      line.setAttribute('y1', espanaCoords.y.toString());
      line.setAttribute('x2', (pais.coordenadas.x - 20).toString()); // Dejamos espacio para la punta
      line.setAttribute('y2', pais.coordenadas.y.toString());
      line.setAttribute('stroke', pais.color);
      line.setAttribute('stroke-width', '2');
      group.appendChild(line);

      // Crear la punta de la flecha
      const arrowHead = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const arrowSize = 15;
      const arrowX = pais.coordenadas.x;
      const arrowY = pais.coordenadas.y;
      
      // Calcular los puntos de la punta de la flecha
      const arrowAngle1 = angle - 30;
      const arrowAngle2 = angle + 30;
      const rad1 = arrowAngle1 * Math.PI / 180;
      const rad2 = arrowAngle2 * Math.PI / 180;
      
      const x1 = arrowX - arrowSize * Math.cos(rad1);
      const y1 = arrowY - arrowSize * Math.sin(rad1);
      const x2 = arrowX - arrowSize * Math.cos(rad2);
      const y2 = arrowY - arrowSize * Math.sin(rad2);
      
      const d = `M ${arrowX} ${arrowY} L ${x1} ${y1} L ${x2} ${y2} Z`;
      arrowHead.setAttribute('d', d);
      arrowHead.setAttribute('fill', pais.color);
      group.appendChild(arrowHead);

      // Crear el efecto glow para la punta
      const glow = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      glow.setAttribute('cx', arrowX.toString());
      glow.setAttribute('cy', arrowY.toString());
      glow.setAttribute('r', '12');
      glow.setAttribute('fill', pais.color);
      glow.setAttribute('filter', 'url(#glow)');
      glow.style.opacity = '0.5';
      group.appendChild(glow);

      // Añadir nombre del país
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', (pais.coordenadas.x + 20).toString());
      text.setAttribute('y', pais.coordenadas.y.toString());
      text.setAttribute('fill', 'white');
      text.setAttribute('font-size', '14');
      text.setAttribute('font-weight', 'bold');
      text.textContent = pais.nombre;
      group.appendChild(text);

      // Animar el grupo completo
      gsap.to(group, {
        opacity: 1,
        duration: 1,
        delay: paisesDestino.indexOf(pais) * 0.2,
        onComplete: () => {
          // Animar el glow
          gsap.to(glow, {
            opacity: 0.8,
            duration: 1,
            repeat: -1,
            yoyo: true,
            ease: 'power1.inOut'
          });
        }
      });
    });
  }, []);

  return (
    <div className="fixed inset-0 bg-gray-900">
      <svg
        ref={svgRef}
        className="w-full h-full"
        viewBox="0 0 1000 600"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Definición del filtro glow */}
        <defs>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Fondo del mapa */}
        <rect width="1000" height="600" fill="#1a1a1a" />
        
        {/* Grupo para las flechas */}
        <g ref={linesRef} className="arrows-container" />
        
        {/* Punto de España */}
        <circle
          cx={espanaCoords.x}
          cy={espanaCoords.y}
          r="8"
          fill="#FFD700"
        />
        <text
          x={espanaCoords.x + 15}
          y={espanaCoords.y}
          fill="white"
          fontSize="16"
          fontWeight="bold"
        >
          España
        </text>
      </svg>
    </div>
  );
};

export default EnviosPagos; 